import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useQuery } from './useQuery';
import type { Project, DiversSubProject } from '../types';

export { SIEGE_COORDS, getEntityColors, calculateDistance, determineZone } from '../utils/geoUtils';

export interface SubProjectFormData {
  divers_number: string;
  name: string;
  address: string;
  lat: number | undefined;
  lng: number | undefined;
  zone: string | number | undefined;
  parent_project_id: string;
}

export interface DiversFormData {
  code: string;
  name: string;
  parent_entity: string;
}

export function useProjectsData() {
  const [mutLoading, setLoading] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Données chargées via useQuery
  const { data: projects, loading: queryLoading, refresh: refreshProjects } = useQuery<any>(
    () => supabase
      .from('projects')
      .select('*')
      .neq('code', 'SYS-FORM')
      .order('is_active', { ascending: false })
      .order('code', { ascending: false }),
    []
  );

  const { data: subProjects, refresh: refreshSubProjects } = useQuery<any>(
    () => supabase
      .from('divers_sub_projects')
      .select('*')
      .order('created_at', { ascending: false }),
    []
  );

  const loading = mutLoading || queryLoading;

  const loadProjects = async () => {
    await Promise.all([refreshProjects(), refreshSubProjects()]);
  };

  const [viewMode, setViewMode] = useState<'active' | 'archived'>('active');
  const [expandedDivers, setExpandedDivers] = useState<Set<string>>(new Set());

  // Formulaire d'édition/création
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Project>>({
    code: '',
    name: '',
    estimated_hours: 0,
    estimated_hours_be: 0,
    is_active: true,
    address: '',
    lat: undefined,
    lng: undefined,
    zone: undefined,
    gd_amount: undefined,
    gd_travel_hours: undefined,
    devis_amount: undefined
  });

  // Formulaire création groupe Divers
  const [showDiversForm, setShowDiversForm] = useState(false);
  const [diversFormData, setDiversFormData] = useState<DiversFormData>({
    code: '',
    name: '',
    parent_entity: ''
  });

  // Formulaire création sous-chantier Divers
  const [showSubProjectModal, setShowSubProjectModal] = useState(false);
  const [subProjectFormData, setSubProjectFormData] = useState<SubProjectFormData>({
    divers_number: '',
    name: '',
    address: '',
    lat: undefined,
    lng: undefined,
    zone: undefined,
    parent_project_id: ''
  });
  const [isGeocodingSubProject, setIsGeocodingSubProject] = useState(false);
  const [isSavingSubProject, setIsSavingSubProject] = useState(false);

  // --- Computed ---
  const filteredProjects = (projects as Project[]).filter(p => {
    const matchActive = viewMode === 'active' ? p.is_active : !p.is_active;
    return matchActive && p.category !== 'divers';
  });

  const diversProjects = (projects as Project[]).filter(p => {
    const matchActive = viewMode === 'active' ? p.is_active : !p.is_active;
    return matchActive && p.category === 'divers';
  });

  const archivedSubProjects = (subProjects as DiversSubProject[]).filter(sp => !sp.is_active);

  const toggleDiversExpanded = (projectId: string) => {
    setExpandedDivers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) newSet.delete(projectId);
      else newSet.add(projectId);
      return newSet;
    });
  };

  const getSubProjectsForParent = (parentId: string) => {
    return (subProjects as DiversSubProject[]).filter(sp => {
      const matchParent = sp.parent_project_id === parentId;
      const matchActive = viewMode === 'active' ? sp.is_active : !sp.is_active;
      return matchParent && matchActive;
    });
  };

  const getParentDiversNumber = (parentId: string): string => {
    const parentProject = (projects as Project[]).find(p => p.id === parentId);
    return parentProject?.code || '';
  };

  return {
    // raw data
    projects: projects as Project[],
    subProjects: subProjects as DiversSubProject[],
    loading,
    setLoading,
    isGeocoding,
    setIsGeocoding,
    loadProjects,
    // view
    viewMode,
    setViewMode,
    expandedDivers,
    setExpandedDivers,
    toggleDiversExpanded,
    // form édition
    editingId,
    setEditingId,
    formData,
    setFormData,
    // form divers
    showDiversForm,
    setShowDiversForm,
    diversFormData,
    setDiversFormData,
    // form sub-project
    showSubProjectModal,
    setShowSubProjectModal,
    subProjectFormData,
    setSubProjectFormData,
    isGeocodingSubProject,
    setIsGeocodingSubProject,
    isSavingSubProject,
    setIsSavingSubProject,
    // computed
    filteredProjects,
    diversProjects,
    archivedSubProjects,
    getSubProjectsForParent,
    getParentDiversNumber,
  };
}
